import { gql } from '@apollo/client';

/** What a screen shows about one of an employee's numbers. */
const CONTACT_NUMBER_FIELDS = `
  id
  number
  label
  receivesTexts
  status
  confirmedAt
  addedBy
`;

/** What the consent page needs back after each change to a number. */
const CONSENT_PAGE_EMPLOYEE_FIELDS = `
  id
  companyId
  firstName
  mobileNumber
  hasAuthorizedReceivingSmsMessages
  multiplePhoneNumbers
  phoneNumbers {
    ${CONTACT_NUMBER_FIELDS}
  }
`;

export const ADD_EMPLOYEE = gql`
  mutation AddEmployee($input: CreateEmployeeInput!) {
    createEmployee(input: $input) {
      employeeId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
      phoneNumbers {
        ${CONTACT_NUMBER_FIELDS}
      }
    }
  }
`;

export const GET_EMPLOYEE_BY_RECORD_ID = gql`
  query GetEmployeeByRecordId($recordId: ID!) {
    employeeByRecordId(recordId: $recordId) {
      id
      employeeId
      companyId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
    }
  }
`;

export const GET_EMPLOYEE_BY_EMPLOYEE_ID_AND_FRIENDLY_COMPANY_ID_NO_AUTH = gql`
  query GetEmployeeByEmployeeIdAndFriendlyCompanyIdNoAuth(
    $employeeId: String!
    $friendlyCompanyId: String!
  ) {
    employeeByEmployeeIdAndFriendlyCompanyIdNoAuth(
      employeeId: $employeeId
      friendlyCompanyId: $friendlyCompanyId
    ) {
      id
      companyId
      firstName
      mobileNumber
      hasAuthorizedReceivingSmsMessages
      multiplePhoneNumbers
      phoneNumbers {
        ${CONTACT_NUMBER_FIELDS}
      }
    }
  }
`;

export const GET_EMPLOYEE_BY_ID = gql`
  query GetEmployeeById($employeeId: String!) {
    employee(employeeId: $employeeId) {
      id
      employeeId
      companyId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
    }
  }
`;

export const GET_EMPLOYEE_BY_ID_AND_COMPANY_ID = gql`
  query GetEmployeeByIdAndCompanyId($employeeId: String!, $companyId: String!) {
    employeeByEmployeeIdAndCompanyIdNoAuth(
      employeeId: $employeeId
      companyId: $companyId
    ) {
      ... on Employee {
        id
        employeeId
        companyId
        firstName
        lastName
        emailAddress
        mobileNumber
        department
        managementRoles {
          id
          name
        }
        hasAuthorizedReceivingSmsMessages
      }
      ... on AnonymousEmployee {
        id
        employeeId
        companyId
        employeeDepartmentWhenCreated
      }
    }
  }
`;

export const GET_EMPLOYEES_CONNECTION = gql`
  query GetEmployeesConnection($first: Int!, $after: String) {
    employeesConnection(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          employeeId
          companyId
          firstName
          lastName
          emailAddress
          mobileNumber
          department
          archivedDate
          managementRoles {
            id
            name
          }
          hasAuthorizedReceivingSmsMessages
          phoneNumbers {
            ${CONTACT_NUMBER_FIELDS}
          }
          phoneConsentRequestedAt
          emailBounce {
            at
            type
            reason
          }
          createdAt
          updatedAt
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_EMPLOYEES_FOR_CURRENT_COMPANY = gql`
  query GetEmployeesForCurrentCompany {
    employees {
      id
      employeeId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
    }
  }
`;

export const GET_EMPLOYEES_FOR_CURRENT_COMPANY_BY_IDS = gql`
  query GetEmployeesForCurrentCompanyByIds($ids: [String!]!) {
    employeesByIds(ids: $ids) {
      id
      employeeId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
    }
  }
`;

export const UPDATE_EMPLOYEE_SMS_CONSENT = gql`
  mutation UpdateEmployeeSmsConsent(
    $id: ID!
    $input: UpdateEmployeeSmsConsentInput!
  ) {
    updateEmployeeSmsConsent(id: $id, input: $input) {
      id
      employeeId
      companyId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
    }
  }
`;

export const UPDATE_EMPLOYEE = gql`
  mutation UpdateEmployee($id: ID!, $input: UpdateEmployeeInput!) {
    updateEmployee(id: $id, input: $input) {
      id
      employeeId
      companyId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
      phoneNumbers {
        ${CONTACT_NUMBER_FIELDS}
      }
    }
  }
`;

export const DELETE_EMPLOYEE = gql`
  mutation DeleteEmployee($id: ID!) {
    deleteEmployee(id: $id) {
      id
      employeeId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
    }
  }
`;

export const GET_ACTIVE_EMPLOYEES = gql`
  query GetActiveEmployees {
    activeEmployees {
      id
      employeeId
      firstName
      lastName
      emailAddress
      mobileNumber
      department
      managementRoles {
        id
        name
      }
      hasAuthorizedReceivingSmsMessages
    }
  }
`;

export const GET_EMPLOYEES_COUNT = gql`
  query GetEmployeesCount {
    employeesCount {
      count
    }
  }
`;

/** The consent page's own mutations: public, and checked against the company. */
export const ADD_EMPLOYEE_PHONE_NUMBER = gql`
  mutation AddEmployeePhoneNumber(
    $id: ID!
    $companyId: ID!
    $number: PhoneNumber!
    $label: PhoneNumberLabel
  ) {
    addEmployeePhoneNumber(id: $id, companyId: $companyId, number: $number, label: $label) {
      ${CONSENT_PAGE_EMPLOYEE_FIELDS}
    }
  }
`;

export const SEND_PHONE_VERIFICATION_CODE = gql`
  mutation SendPhoneVerificationCode($id: ID!, $companyId: ID!, $phoneNumberId: ID!) {
    sendPhoneVerificationCode(id: $id, companyId: $companyId, phoneNumberId: $phoneNumberId)
  }
`;

export const CONFIRM_PHONE_VERIFICATION_CODE = gql`
  mutation ConfirmPhoneVerificationCode(
    $id: ID!
    $companyId: ID!
    $phoneNumberId: ID!
    $code: String!
  ) {
    confirmPhoneVerificationCode(
      id: $id
      companyId: $companyId
      phoneNumberId: $phoneNumberId
      code: $code
    ) {
      ${CONSENT_PAGE_EMPLOYEE_FIELDS}
    }
  }
`;

export const STOP_TEXTS_TO_PHONE_NUMBER = gql`
  mutation StopTextsToPhoneNumber($id: ID!, $companyId: ID!, $phoneNumberId: ID!) {
    stopTextsToPhoneNumber(id: $id, companyId: $companyId, phoneNumberId: $phoneNumberId) {
      ${CONSENT_PAGE_EMPLOYEE_FIELDS}
    }
  }
`;

export const REMOVE_EMPLOYEE_PHONE_NUMBER = gql`
  mutation RemoveEmployeePhoneNumber($id: ID!, $companyId: ID!, $phoneNumberId: ID!) {
    removeEmployeePhoneNumber(id: $id, companyId: $companyId, phoneNumberId: $phoneNumberId) {
      ${CONSENT_PAGE_EMPLOYEE_FIELDS}
    }
  }
`;

/** The admin's "Send consent link" button. False: nothing sent (nothing to confirm, or sent today). */
export const SEND_EMPLOYEE_CONSENT_LINK = gql`
  mutation SendEmployeeConsentLink($id: ID!) {
    sendEmployeeConsentLink(id: $id)
  }
`;
